import { useEffect } from "react";
import { isEmpty } from "lodash";
import { useFormContext } from "react-hook-form";
import { t } from "@/i18n-js/instance";
import { useFieldElementRules } from "@/react/components/FormRenderer/hooks/useFieldElementRules";
import type { FieldElement as FieldElementType } from "@/react/types/Element";
import { Form } from "@circle-react-uikit/Form";

interface FieldElementProps {
  element: FieldElementType;
  errors: Record<number, string>;
  fieldIndex: number;
  initialFocusRef?: React.RefObject<HTMLInputElement>;
}
export const FieldElement = ({
  element,
  initialFocusRef,
  fieldIndex,
  errors,
}: FieldElementProps) => {
  const { setError } = useFormContext();
  const rules = useFieldElementRules(element);
  let label = element.attributes.label.trim();

  if (element.attributes.field_type === "contact_email") {
    // When the field type is "contact_email", we force "email" if not provided.
    label = label || t("settings.forms.default_contact_email_label");
  } else if (element.attributes.field_type === "contact_name") {
    // When the field type is "contact_name", we also force a default label if not provided.
    label = label || t("settings.forms.default_contact_name_label");
  }

  const idName = `elements[${fieldIndex}][id]`;
  const valueName = `elements[${fieldIndex}][value]`;

  useEffect(() => {
    if (errors && errors[fieldIndex]) {
      setError(`elements[${fieldIndex}][value]`, {
        type: "server",
        message: errors[fieldIndex],
      });
    }
  }, [errors, fieldIndex, setError]);

  if (element.attributes.hidden) {
    return null;
  }

  return (
    <>
      <Form.Item
        id={valueName}
        name={valueName}
        hideDescription
        hideBorder
        hideLabel={isEmpty(label)}
        label={label}
        rules={rules}
        forceRequiredLabel={element.attributes.required}
        errorClassName="form-group__error !mb-0"
        placeholder={
          element.attributes.placeholder ||
          t("settings.forms.edit.defaults.field_placeholder", {
            field_label: label.toLowerCase(),
          })
        }
      >
        <Form.Input
          type="text"
          name={valueName}
          autoComplete="off"
          ref={initialFocusRef}
        />
      </Form.Item>
      <Form.Input type="hidden" name={idName} value={element.id} />
    </>
  );
};
